import { FilterOutlined } from '@ant-design/icons'
import { List, Typography } from 'antd'
import { useDrilldownSchema } from 'features/drilldown'
import { useGetPrecedingFilters } from 'features/drilldown/data/DrilldownFilterContext'
import { OPERATOR_NAMES } from 'features/drilldown/panels/global/filters/DrilldownFilterRow'
import _ from 'lodash'
import React, { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { RolodexHistogramFilter } from 'types'

export const LATEST_DATA_SCHEMA_VERSION = 1
export const DEFAULT_ACTIVE_FILTERS_PANEL_DATA = {
  title: 'Active Filters',
  isPinned: false,
  panelDataSchemaVersion: LATEST_DATA_SCHEMA_VERSION,
}

const { Text } = Typography

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const StyledList = styled(List)`
  .ant-list-item {
    margin-left: 1.5rem;
  }

  .ant-list-item-meta-avatar {
    justify-self: center;
    align-self: center;
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: ${CantileverTheme.colors.grayscale[400]};
`

const getFilterType = (filter: RolodexHistogramFilter): string =>
  filter.type || 'unknown'

export const ActiveFiltersPanel: FC = () => {
  const precedingFilters = useGetPrecedingFilters()
  const { isDescendant } = useDrilldownSchema()

  const formatFilterValue = useCallback(
    (filter: RolodexHistogramFilter): string => {
      const { op, value, field, key, type } = filter
      const fieldName = field || key || 'unknown'
      const typeName = type || 'unknown'
      const opDisplay = _.toLower(OPERATOR_NAMES[op] || op)

      if (!value) {
        return `${typeName}.${fieldName} ${opDisplay}`
      }

      if (Array.isArray(value)) {
        if (value.length === 0) {
          return `${typeName}.${fieldName} ${opDisplay} (empty)`
        }
        if (value.length === 1) {
          return `${typeName}.${fieldName} ${opDisplay} ${value[0]}`
        }
        return `${typeName}.${fieldName} ${opDisplay} [${value.slice(0, 3).join(', ')}${value.length > 3 ? `, +${value.length - 3} more` : ''}]`
      }

      return `${typeName}.${fieldName} ${opDisplay} ${value}`
    },
    []
  )

  const renderFilterItem = useCallback(
    (filter: unknown) => {
      const typedFilter = filter as RolodexHistogramFilter // This can't be typed at the function level or the antd list complains
      return (
        <List.Item key={`${typedFilter.key}`}>
          <List.Item.Meta
            avatar={
              <FilterOutlined
                style={{ color: CantileverTheme.colors.grayscale[500] }}
              />
            }
            title={typedFilter.type}
            description={formatFilterValue(typedFilter)}
          />
        </List.Item>
      )
    },
    [formatFilterValue]
  )

  const activeFilters = useMemo(() => {
    return precedingFilters.filter((filter) => {
      if (!filter.value) return false
      if (Array.isArray(filter.value) && filter.value.length === 0) return false
      return true
    })
  }, [precedingFilters])

  const sortedFilters = useMemo(() => {
    const types = _.uniq(activeFilters.map(getFilterType))
    const sortedTypes = types.sort((a, b) => {
      if (a === b) return 0
      if (isDescendant(b, a)) return -1
      if (isDescendant(a, b)) return 1
      return 0
    })
    return _.sortBy(activeFilters, (f) => sortedTypes.indexOf(getFilterType(f)))
  }, [activeFilters, isDescendant])

  const hasActiveFilters = sortedFilters.length > 0

  if (!hasActiveFilters) {
    return (
      <Container>
        <EmptyState>
          <FilterOutlined style={{ fontSize: 24, marginBottom: 8 }} />
          <Text>No active filters</Text>
          <Text type='secondary' style={{ fontSize: 12 }}>
            Add filters in other panels to see them here
          </Text>
        </EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      <StyledList dataSource={sortedFilters} renderItem={renderFilterItem} />
    </Container>
  )
}
